import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { useEffect, useState } from "react";

import {
  flushDocumentSaves,
  importGuestDocumentsToAccount,
} from "../documents/DocumentManager";
import { documentStorage } from "../documents/documentStorage";
import { flushCommentWrites } from "../comments/commentStorage";

import { useAtom } from "../app-jotai";

import { authClient, authURL, FORCE_GUEST_MODE_KEY } from "./authClient";

import { accountDialogOpenAtom } from "./accountState";

import "./AuthPanel.scss";

export const AuthDialog = ({ onClose }: { onClose: () => void }) => {
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [googleEnabled, setGoogleEnabled] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${authURL.replace(/\/$/, "")}/api/v2/auth/providers`, {
      signal: controller.signal,
    })
      .then(async (response) => {
        if (response.ok) {
          const providers = await response.json();
          if (!controller.signal.aborted) {
            setGoogleEnabled(providers.google === true);
          }
        }
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  const signInWithGoogle = async () => {
    setSubmitting(true);
    setError("");
    try {
      await Promise.all([flushDocumentSaves(), flushCommentWrites()]);
      const result = await authClient.signIn.social({
        provider: "google",
        callbackURL: window.location.href,
      });
      if (result.error) {
        setError(result.error.message || "Could not sign in with Google");
        setSubmitting(false);
        return;
      }
      localStorage.removeItem(FORCE_GUEST_MODE_KEY);
    } catch {
      setError("Could not connect to Google sign-in. Please try again.");
      setSubmitting(false);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    const result =
      mode === "signup"
        ? await authClient.signUp.email({ name, email, password })
        : await authClient.signIn.email({ email, password });
    setSubmitting(false);
    if (result.error) {
      setError(result.error.message || "Authentication failed");
      return;
    }
    localStorage.removeItem(FORCE_GUEST_MODE_KEY);
    onClose();
  };

  return (
    <Dialog
      title={mode === "signin" ? "Sign in" : "Create account"}
      size="small"
      onCloseRequest={onClose}
    >
      <form className="auth-dialog" onSubmit={submit}>
        <p>
          {mode === "signin"
            ? "Sign in to access your drawings on this device."
            : "Create an account to save your drawings across devices."}
        </p>
        {googleEnabled && (
          <button
            type="button"
            className="auth-dialog__google"
            disabled={submitting}
            onClick={signInWithGoogle}
          >
            Continue with Google
          </button>
        )}
        {mode === "signup" && (
          <label>
            Name
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoComplete="name"
              required
            />
          </label>
        )}
        <label>
          Email
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete={
              mode === "signin" ? "current-password" : "new-password"
            }
            minLength={8}
            required
          />
        </label>
        {error && <div className="auth-dialog__error">{error}</div>}
        <button type="submit" disabled={submitting}>
          {submitting
            ? "Please wait..."
            : mode === "signin"
            ? "Sign in"
            : "Create account"}
        </button>
        <button
          type="button"
          className="auth-dialog__switch"
          onClick={() => {
            setMode(mode === "signin" ? "signup" : "signin");
            setError("");
          }}
        >
          {mode === "signin"
            ? "Need an account? Sign up"
            : "Already have an account? Sign in"}
        </button>
      </form>
    </Dialog>
  );
};

export const AuthPanel = () => {
  const [, setAccountOpen] = useAtom(accountDialogOpenAtom);
  const session = authClient.useSession();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState("");
  const forceGuestMode = localStorage.getItem(FORCE_GUEST_MODE_KEY) === "true";

  const useLocalDrawings = async () => {
    await Promise.all([flushDocumentSaves(), flushCommentWrites()]);
    localStorage.setItem(FORCE_GUEST_MODE_KEY, "true");
    window.location.reload();
  };

  const returnToAccount = async () => {
    await Promise.all([flushDocumentSaves(), flushCommentWrites()]);
    localStorage.removeItem(FORCE_GUEST_MODE_KEY);
    window.location.reload();
  };

  const signOut = async () => {
    setSigningOut(true);
    setSignOutError("");
    await Promise.all([flushDocumentSaves(), flushCommentWrites()]);
    const result = await authClient.signOut();
    setSigningOut(false);
    if (result.error) {
      setSignOutError(result.error.message || "Could not sign out");
      return;
    }
    localStorage.removeItem(FORCE_GUEST_MODE_KEY);
  };

  if (session.data?.user && forceGuestMode) {
    return (
      <div className="auth-panel auth-panel--guest">
        <span>Local drawings</span>
        <button type="button" onClick={() => setAccountOpen(true)}>
          Account
        </button>
        <button type="button" onClick={returnToAccount}>
          Return to account
        </button>
        <button type="button" disabled={signingOut} onClick={signOut}>
          {signingOut ? "Signing out..." : "Sign out"}
        </button>
        {signOutError && <span>{signOutError}</span>}
      </div>
    );
  }

  if (!session.data?.user && documentStorage.getScope() !== "guest") {
    return (
      <div className="auth-panel auth-panel--guest">
        <span>Offline account drawings</span>
        <button type="button" onClick={useLocalDrawings}>
          Use local
        </button>
      </div>
    );
  }

  if (session.data?.user) {
    return (
      <div className="auth-panel">
        <div className="auth-panel__identity">
          <strong>{session.data.user.name}</strong>
          <span>{session.data.user.email}</span>
        </div>
        <div className="auth-panel__actions">
          <button type="button" onClick={() => setAccountOpen(true)}>
            Account
          </button>
          <button
            type="button"
            disabled={importing}
            onClick={async () => {
              if (
                !window.confirm("Import your local drawings into this account?")
              ) {
                return;
              }
              setImporting(true);
              try {
                await importGuestDocumentsToAccount();
              } finally {
                setImporting(false);
              }
            }}
          >
            {importing ? "Importing..." : "Import local"}
          </button>
          <button
            type="button"
            disabled={importing || signingOut}
            onClick={useLocalDrawings}
          >
            Use local
          </button>
          <button
            type="button"
            disabled={importing || signingOut}
            onClick={signOut}
          >
            {signingOut ? "Signing out..." : "Sign out"}
          </button>
          {signOutError && <span>{signOutError}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="auth-panel auth-panel--guest">
      <span>Local drawings</span>
      <button type="button" onClick={() => setDialogOpen(true)}>
        Sign in to sync
      </button>
      {dialogOpen && <AuthDialog onClose={() => setDialogOpen(false)} />}
    </div>
  );
};
